/**
 * useBuilderPanelRecordPoll
 *
 * Tracks the post-Save wait in `BuilderPanel`: approval is server-side, so
 * nothing client-side knows when the entity is projected. This hook owns the
 * `awaitingRecord` flag and the poll that asks for the record until it shows
 * up in the store — it does NOT own clearing the flag once the record lands,
 * which is a deliberate render-time conditional state update
 * (docs/claude/REACT_19_PATTERNS.md § 4.2), not an effect, and stays in
 * `BuilderPanel` alongside the `storeObjectIsComplete` read it depends on.
 */
import { useCallback, useEffect, useState } from 'react';

import { ensureEntity } from '@/lib/crm/object-cards/entityFetchBatcher';

/**
 * How often to re-ask for the record while waiting for the worker.
 *
 * This is a poll for a thing that WILL exist, not a guess at how long to wait:
 * it stops the moment the record lands. Approval is server-side, so nothing
 * client-side knows when the entity is projected — the store's own
 * pending-creation recovery only covers optimistic client writes.
 */
const RECORD_POLL_MS = 500;

interface BuilderPanelRecordPoll {
  /** True while the panel is waiting for a just-approved record to land. */
  awaitingRecord: boolean;
  /** Enter the wait — called when Save succeeds. */
  startAwaitingRecord: () => void;
  /** Leave the wait — called once the record lands in the store. */
  clearAwaitingRecord: () => void;
}

export function useBuilderPanelRecordPoll(
  entityId: string | undefined,
  resolvedOrgId: number | undefined
): BuilderPanelRecordPoll {
  const [awaitingRecord, setAwaitingRecord] = useState(false);

  const startAwaitingRecord = useCallback(() => {
    setAwaitingRecord(true);
  }, []);
  const clearAwaitingRecord = useCallback(() => {
    setAwaitingRecord(false);
  }, []);

  // Ask for the record until it exists. Approval is server-side, so the store
  // has nothing to recover from on its own; without this the panel waits on a
  // store update that never comes.
  useEffect(() => {
    if (!awaitingRecord || !entityId || !resolvedOrgId) {
      return;
    }
    ensureEntity(resolvedOrgId, entityId);
    const poll = setInterval(
      () => ensureEntity(resolvedOrgId, entityId),
      RECORD_POLL_MS
    );
    return () => clearInterval(poll);
  }, [awaitingRecord, entityId, resolvedOrgId]);

  return { awaitingRecord, startAwaitingRecord, clearAwaitingRecord };
}
